// src/services/transcriptionApi.js
import { Auth } from 'aws-amplify';

const TRANSCRIPTION_CONFIG = {
  prod: {
    httpUrl: 'https://conversation.agentdeveloper.agent-enterprise.com',
    wsUrl:   'wss://conversation.agentdeveloper.agent-enterprise.com'
  },
  dev: {
    httpUrl: 'https://conversation-dev.agentdeveloper.agent-enterprise.com',
    wsUrl:   'wss://conversation-dev.agentdeveloper.agent-enterprise.com'
  }
};

const getConfig = (environment = 'prod') =>
  TRANSCRIPTION_CONFIG[environment] || TRANSCRIPTION_CONFIG.prod;

// ─────────────────────────────────────────────────────────────────────────────
// Method 1: HTTP — async job (returns jobId, polls separately)
// Use this for files > 30 seconds
// ─────────────────────────────────────────────────────────────────────────────
export const transcribeViaHttp = async (audioBlob, options = {}) => {
  const {
    language    = 'en-US',
    environment = 'prod',
    sessionId   = null,
    async       = false   // set true to skip polling and return jobId
  } = options;

  const session  = await Auth.currentSession();
  const idToken  = session.getIdToken().getJwtToken();
  const config   = getConfig(environment);
  const base64Audio = await blobToBase64(audioBlob);

  const payload = {
    audio:     base64Audio,
    mimeType:  audioBlob.type || 'audio/webm',
    language,
    async,                // tell Lambda not to poll — just start job and return
    ...(sessionId && { sessionId })
  };

  const response = await fetch(`${config.httpUrl}/transcribe`, {
    method:  'POST',
    headers: {
      'Authorization': idToken,
      'Content-Type':  'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Transcription failed: ${response.status}`);
  }

  const data   = await response.json();
  const parsed = data.body ? JSON.parse(data.body) : data;

  if (!parsed.success) {
    throw new Error(parsed.error || 'Transcription returned no result');
  }

  // async mode: caller gets jobId and polls separately
  if (async) {
    return { jobId: parsed.jobId, status: 'started' };
  }

  return parsed.transcription || parsed.text || '';
};

// ─────────────────────────────────────────────────────────────────────────────
// Method 2: Poll for async job result
// ─────────────────────────────────────────────────────────────────────────────
export const pollTranscriptionJob = async (jobId, options = {}) => {
  const {
    environment   = 'prod',
    intervalMs    = 2000,
    maxAttempts   = 60,
    onProgress    = null
  } = options;

  const session = await Auth.currentSession();
  const idToken = session.getIdToken().getJwtToken();
  const config  = getConfig(environment);

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await sleep(intervalMs);

    const response = await fetch(`${config.httpUrl}/transcribe/status`, {
      method:  'POST',
      headers: {
        'Authorization': idToken,
        'Content-Type':  'application/json'
      },
      body: JSON.stringify({ jobId })
    });

    const data   = await response.json();
    const parsed = data.body ? JSON.parse(data.body) : data;

    onProgress?.(attempt, maxAttempts, parsed.status);

    if (parsed.status === 'COMPLETED') {
      return parsed.transcription || '';
    }

    if (parsed.status === 'FAILED') {
      throw new Error(parsed.error || 'Transcription job failed');
    }
    // else IN_PROGRESS — keep polling
  }

  throw new Error('Transcription polling timed out');
};

// ─────────────────────────────────────────────────────────────────────────────
// Method 3: WebSocket streaming — fastest, real-time partial results
// Backend proxies audio chunks → AWS Transcribe Streaming
// ─────────────────────────────────────────────────────────────────────────────
export const transcribeViaWebSocket = (options = {}) => {
  const {
    language           = 'en-US',
    environment        = 'prod',
    sessionId          = null,
    onPartialTranscript = null,
    onFinalTranscript   = null,
    onError             = null,
    onConnected         = null,
    onDisconnected      = null
  } = options;

  const config = getConfig(environment);
  let ws            = null;
  let isConnected   = false;
  let chunkSequence = 0;
  let reconnectTimer = null;

  const connect = async () => {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken().getJwtToken();

    const wsUrl = new URL(`${config.wsUrl}/transcribe/stream`);
    wsUrl.searchParams.set('token',    idToken);
    wsUrl.searchParams.set('language', language);
    if (sessionId) wsUrl.searchParams.set('sessionId', sessionId);

    return new Promise((resolve, reject) => {
      ws = new WebSocket(wsUrl.toString());
      ws.binaryType = 'arraybuffer';

      const connectionTimeout = setTimeout(() => {
        if (!isConnected) reject(new Error('WebSocket connection timeout'));
      }, 10000);

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        isConnected = true;

        ws.send(JSON.stringify({
          action:    'transcribe_init',
          language,
          sessionId,
          timestamp: new Date().toISOString()
        }));

        onConnected?.();
        resolve();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          switch (data.type) {
            case 'partial_transcript':
              onPartialTranscript?.(data.text);
              break;
            case 'final_transcript':
              onFinalTranscript?.(data.text, data.confidence);
              break;
            case 'error':
              onError?.(new Error(data.message));
              break;
            case 'ready':
              console.log('🎤 Transcription WS ready');
              break;
            default:
              console.log('Unknown transcription WS msg:', data.type);
          }
        } catch (err) {
          console.error('Error parsing transcription WS message:', err);
        }
      };

      ws.onerror = (err) => {
        isConnected = false;
        clearTimeout(connectionTimeout);
        onError?.(new Error('WebSocket transcription error'));
        reject(new Error('WebSocket transcription error'));
      };

      ws.onclose = (event) => {
        isConnected = false;
        onDisconnected?.(event.code);
        if (event.code !== 1000) {
          onError?.(new Error(`WS closed unexpectedly: ${event.code}`));
        }
      };
    });
  };

  const sendAudioChunk = (chunk) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    const sendBase64 = (b64) => {
      ws.send(JSON.stringify({
        action:    'audio_chunk',
        audio:     b64,
        sequence:  chunkSequence++,
        timestamp: Date.now()
      }));
    };

    if (chunk instanceof ArrayBuffer) {
      sendBase64(arrayBufferToBase64(chunk));
    } else if (chunk instanceof Blob) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (ws?.readyState === WebSocket.OPEN) {
          sendBase64(reader.result.split(',')[1]);
        }
      };
      reader.readAsDataURL(chunk);
    }
  };

  const finalize = () => {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        action:    'transcribe_end',
        timestamp: Date.now()
      }));
    }
  };

  const disconnect = () => {
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (ws) {
      ws.close(1000, 'Recording complete');
      ws = null;
    }
    isConnected = false;
  };

  return {
    connect,
    sendAudioChunk,
    finalize,
    disconnect,
    isConnected: () => isConnected
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Smart transcriber — picks the right method based on blob size / duration
// ─────────────────────────────────────────────────────────────────────────────
export const transcribeAudioSmart = async (audioBlob, options = {}) => {
  const {
    environment    = 'prod',
    sessionId      = null,
    language       = 'en-US',
    onProgress     = null,
    shortThreshold = 500_000   // bytes — under this use sync HTTP
  } = options;

  if (audioBlob.size <= shortThreshold) {
    // Small file — synchronous HTTP, fastest round-trip
    return transcribeViaHttp(audioBlob, { language, environment, sessionId, async: false });
  }

  // Large file — fire async job, then poll so we never hit API GW timeout
  console.log(`📦 Large audio (${(audioBlob.size / 1024).toFixed(0)} KB) — using async job`);

  const { jobId } = await transcribeViaHttp(audioBlob, {
    language,
    environment,
    sessionId,
    async: true    // Lambda starts job and returns jobId immediately
  });

  return pollTranscriptionJob(jobId, {
    environment,
    intervalMs:  2500,
    maxAttempts: 80,   // 80 × 2.5s = 3.3 min max
    onProgress:  (attempt, max, status) => {
      onProgress?.(Math.round((attempt / max) * 100), status);
    }
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────
const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror   = reject;
    reader.readAsDataURL(blob);
  });

const arrayBufferToBase64 = (buffer) => {
  const bytes  = new Uint8Array(buffer);
  let   binary = '';
  bytes.forEach(b => binary += String.fromCharCode(b));
  return window.btoa(binary);
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const getExtensionFromMimeType = (mimeType = '') => {
  const map = {
    'audio/webm': 'webm',
    'audio/ogg':  'ogg',
    'audio/mp4':  'mp4',
    'audio/mpeg': 'mp3',
    'audio/wav':  'wav'
  };
  return map[mimeType.split(';')[0]] || 'webm';
};

export default {
  transcribeViaHttp,
  pollTranscriptionJob,
  transcribeViaWebSocket,
  transcribeAudioSmart
};
